// One proposed write, reviewed on its own: an editable control per changed
// field with the change shown inline (word diff for text, +/− delta for
// multiselects, "was:" hint for selects), plus a "Respond to the agent" box
// that routes the item to a revision request.
import React, { useMemo } from 'react';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import {
  getArgValue,
  getFieldConfig,
  getFieldLabel,
  humanizeFieldName,
  isPlainObject,
  type FieldConfig,
  type SelectOption,
} from '../../confirmationFieldConfig';
import type { ItemState, PendingConfirmation, ReviewUnit } from '../types';
import ConfirmationField, {
  asEditableText,
  formatValue,
} from './ConfirmationField';
import { useAorText } from 'utils/aorTerminology';
import InlineDiffText from './InlineDiffText';

/**
 * +added / −removed summary for a list-valued change (multiselects, structured
 * arrays), computed against the reviewer's current selection so it tracks
 * inline edits. One item per line — list data reads as a list, not prose.
 */
const ListDelta: React.FC<{ before: unknown; current: unknown }> = ({
  before,
  current,
}) => {
  const beforeItems = (Array.isArray(before) ? before : []).map(formatValue);
  const currentItems = (Array.isArray(current) ? current : []).map(formatValue);
  const added = currentItems.filter((v) => !beforeItems.includes(v));
  const removed = beforeItems.filter((v) => !currentItems.includes(v));
  if (added.length === 0 && removed.length === 0) return null;
  return (
    <ul className="tw:m-0 tw:mb-1 tw:list-none tw:p-0 tw:text-[13px]">
      {added.map((v) => (
        <li
          key={`add-${v}`}
          className="tw:break-words tw:text-[color:var(--success-300)]"
        >
          +{v}
        </li>
      ))}
      {removed.map((v) => (
        <li
          key={`del-${v}`}
          className="tw:break-words tw:text-[color:var(--text-weak)] tw:line-through tw:decoration-[var(--error-400)]"
        >
          −{v}
        </li>
      ))}
    </ul>
  );
};

/**
 * Editor for an object-valued field (editKind 'object', e.g. weather): one
 * labeled row per key, each with its own inline diff / inline editor. Edits
 * merge back into the whole object so the field still resumes as one value.
 */
const ObjectDiffRows: React.FC<{
  before: unknown;
  value: unknown;
  label: string;
  onChange: (next: Record<string, unknown>) => void;
}> = ({ before, value, label, onChange }) => {
  const beforeObj = isPlainObject(before) ? before : undefined;
  const valueObj = isPlainObject(value) ? value : {};
  const keys = Array.from(
    new Set([...Object.keys(beforeObj ?? {}), ...Object.keys(valueObj)])
  );
  if (keys.length === 0) {
    return <span className="tw:text-[color:var(--text-weak)]">—</span>;
  }
  return (
    <div>
      {keys.map((key) => {
        const keyBefore =
          beforeObj && key in beforeObj
            ? asEditableText(beforeObj[key])
            : undefined;
        const current = asEditableText(valueObj[key]);
        const editKind: 'text' | 'textarea' =
          (keyBefore ?? '').length > 80 ||
          current.length > 80 ||
          current.includes('\n')
            ? 'textarea'
            : 'text';
        const setKey = (v: string) => onChange({ ...valueObj, [key]: v });
        return (
          <div key={key} className="tw:mb-2">
            <div className="tw:mb-0.5 tw:text-[12px] tw:text-[color:var(--text-weak)]">
              {humanizeFieldName(key)}
            </div>
            {keyBefore !== undefined ? (
              <InlineDiffText
                before={keyBefore}
                value={current}
                editKind={editKind}
                label={`${label} — ${humanizeFieldName(key)}`}
                onChange={setKey}
              />
            ) : (
              <ConfirmationField
                config={{ editKind }}
                options={[]}
                value={current}
                onChange={(v) => setKey(asEditableText(v))}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Array values render one item per line; everything else via formatValue. */
const ValueDisplay: React.FC<{ value: unknown }> = ({ value }) =>
  Array.isArray(value) ? (
    <ul className="tw:m-0 tw:list-none tw:p-0">
      {value.map((v, i) => (
        <li
          key={`val-${i}`}
          className="tw:break-words tw:text-[color:var(--text-white)]"
        >
          {formatValue(v)}
        </li>
      ))}
    </ul>
  ) : (
    <span className="tw:break-words tw:text-[color:var(--text-white)]">
      {formatValue(value)}
    </span>
  );

interface Props {
  item: PendingConfirmation;
  /** The unit under review; a set `field` narrows the card to that one field. */
  unit: ReviewUnit;
  /** 1-based position of this unit among its confirmation's field units. */
  fieldOrdinal?: number;
  /** Total field units of this confirmation (split confirmations only). */
  fieldCount?: number;
  index: number;
  total: number;
  cycle: number;
  state?: ItemState;
  /** Render-time id→name option lists keyed by FieldConfig.dynamicOptions. */
  optionSets: Record<string, SelectOption[]>;
  /**
   * Whether responding is available — a response submits as a reject-with-
   * message, so it must be disabled when the confirmation's allowed_decisions
   * excludes 'reject' (otherwise typed text would submit an unsendable
   * reject).
   */
  canRespond?: boolean;
  onEdit: (field: string, value: unknown) => void;
  onRespond: (text: string) => void;
}

export const ChangeReview: React.FC<Props> = ({
  item,
  unit,
  fieldOrdinal,
  fieldCount,
  index,
  total,
  cycle,
  state,
  optionSets,
  canRespond = true,
  onEdit,
  onRespond,
}) => {
  const aorText = useAorText();
  const entity =
    typeof item.args?.entity === 'string'
      ? (item.args.entity as string)
      : undefined;

  const resolveOptions = (config: FieldConfig): SelectOption[] => {
    const options = config.dynamicOptions
      ? (optionSets[config.dynamicOptions] ?? [])
      : (config.options ?? []);
    if (config.dynamicOptions) return options;
    return options.map((option) => ({
      ...option,
      label: aorText(option.label),
    }));
  };

  // Edits win; else the proposed arg; else the preview's resolved `after` value.
  // The last case covers tools whose preview shows server-side defaults for args
  // the model omitted (e.g. draft_org_event_automation_rule severity) — the
  // editor then starts from what will actually be written instead of blank.
  const editValue = (field: string, fallback?: unknown): unknown => {
    if (state?.edits && field in state.edits) return state.edits[field];
    const argValue = getArgValue(item.args, item.tool_name, field);
    return argValue === undefined ? fallback : argValue;
  };

  const isFieldUnit = unit.field !== undefined;
  const fieldRows = useMemo(
    () =>
      (item.preview?.diff ?? [])
        .filter((d) => !isFieldUnit || d.field === unit.field)
        .map((d) => ({
          field: d.field,
          before: d.before,
          after: d.after,
          label: aorText(getFieldLabel(item.tool_name, entity, d.field)),
          config: getFieldConfig(item.tool_name, entity, d.field, d.after),
        })),
    [item, entity, isFieldUnit, unit.field, aorText]
  );

  const title = aorText(
    isFieldUnit
      ? getFieldLabel(item.tool_name, entity, unit.field!)
      : item.preview?.action || item.tool_name
  );

  return (
    <div>
      <div className="tw:text-[12px] tw:text-[color:var(--text-weak)]">
        Change {index + 1} of {total}
        {cycle > 1 && (
          <span className="tw:text-[color:var(--info-400)]"> · re-review</span>
        )}
      </div>
      <h3
        className={`${isFieldUnit ? 'tw:mb-1' : 'tw:mb-2.5'} tw:mt-0.5 tw:text-[16px] tw:font-medium tw:text-[color:var(--text-white)]`}
      >
        {title}
      </h3>
      {isFieldUnit && (
        <div className="tw:mb-2.5 tw:text-[12px] tw:text-[color:var(--text-weak)]">
          Part of: {item.preview?.action || item.tool_name}
          {fieldOrdinal !== undefined && fieldCount !== undefined && (
            <>
              {' '}
              · field {fieldOrdinal} of {fieldCount}
            </>
          )}
        </div>
      )}
      {item.preview?.irreversible && (
        <span className="badge bg-danger mb-2">Irreversible</span>
      )}

      {fieldRows.length > 0 ? (
        <div>
          {fieldRows.map((row) => (
            <div
              key={row.field}
              className="tw:py-2.5 tw:[&:not(:first-child)]:border-t tw:[&:not(:first-child)]:border-[color:var(--border-neutral)]"
            >
              {!isFieldUnit && (
                <div className="tw:mb-1 tw:text-[12px] tw:font-semibold tw:text-[color:var(--text-weak)]">
                  {row.label}
                </div>
              )}
              {row.config.editKind === 'object' ? (
                <div className="tw:mt-1.5">
                  <ObjectDiffRows
                    before={row.before}
                    value={editValue(row.field, row.after)}
                    label={row.label}
                    onChange={(next) => onEdit(row.field, next)}
                  />
                </div>
              ) : row.config.editKind === 'readonly' ? (
                <>
                  {row.before !== undefined &&
                  (Array.isArray(row.before) || Array.isArray(row.after)) ? (
                    <ListDelta before={row.before} current={row.after} />
                  ) : (
                    row.before !== undefined &&
                    formatValue(row.before) !== formatValue(row.after) && (
                      <div className="tw:break-words tw:text-[14px] tw:leading-[1.55] tw:text-[color:var(--text-white)]">
                        <span className="tw:text-[color:var(--text-weak)] tw:line-through tw:decoration-[var(--error-400)]">
                          {formatValue(row.before)}
                        </span>
                        <span className="tw:text-[color:var(--text-weak)]">
                          {' '}
                          →{' '}
                        </span>
                        <span className="tw:text-[color:var(--success-300)]">
                          {formatValue(row.after)}
                        </span>
                      </div>
                    )
                  )}
                  <ValueDisplay value={row.after} />
                </>
              ) : (row.config.editKind === 'text' ||
                  row.config.editKind === 'textarea') &&
                row.before !== undefined ? (
                <div className="tw:mt-1.5">
                  <InlineDiffText
                    before={asEditableText(row.before)}
                    value={asEditableText(editValue(row.field, row.after))}
                    editKind={row.config.editKind}
                    label={row.label}
                    onChange={(value) => onEdit(row.field, value)}
                  />
                </div>
              ) : (
                <>
                  {row.config.editKind === 'multiselect' &&
                    row.before !== undefined && (
                      <ListDelta
                        before={row.before}
                        current={editValue(row.field, row.after)}
                      />
                    )}
                  {row.config.editKind === 'select' &&
                    row.before !== undefined &&
                    formatValue(row.before) !== formatValue(row.after) && (
                      <div className="tw:mb-1 tw:text-[13px] tw:text-[color:var(--text-weak)]">
                        was: {formatValue(row.before)}
                      </div>
                    )}
                  <div className="tw:mt-1.5">
                    <ConfirmationField
                      config={row.config}
                      options={resolveOptions(row.config)}
                      value={editValue(row.field, row.after)}
                      onChange={(value) => onEdit(row.field, value)}
                    />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="tw:text-[color:var(--text-weak)]">
          No fields to edit — accept to proceed.
        </p>
      )}

      {!!item.preview?.affectedEntities?.length && (
        <p className="tw:mt-3 tw:text-[12px] tw:text-[color:var(--text-weak)]">
          Affects:{' '}
          {item.preview.affectedEntities
            .map((e) => `${e.kind} "${e.label}"`)
            .join(', ')}
        </p>
      )}

      {canRespond && (
        <div className="tw:mt-3.5">
          <label className="form-label tw:text-[13px] tw:text-[color:var(--text-white)]">
            Respond to the agent{' '}
            <span className="tw:text-[color:var(--text-weak)]">(optional)</span>
          </label>
          <StylishNewTextArea
            rows={2}
            value={state?.respondText ?? ''}
            disableLengthMessage
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onRespond(e.target.value)
            }
            placeholder={
              isFieldUnit
                ? 'Type here to send this field back for revision — your edits above are included as context.'
                : 'Type here to send this back for revision — your edits above are included as context.'
            }
          />
        </div>
      )}
    </div>
  );
};

export default ChangeReview;
